/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import indicesImg from 'assets/images/indices.jpeg'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Массивы</h2>
    <p className={styles.description}>
      <b>Массив</b> — структура данных для хранения и манипулирования коллекцией индексированных значений. Используются
      для хранения упорядоченных коллекций данных, например списка курортов, товаров, клиентов в отеле и т. п.
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Создание</h3>
      <p className={styles.description}>
        Синтаксис для создания нового массива — квадратные скобки <code className={styles.code}>[]</code> со списком
        элементов разделенных запятыми. В массиве может храниться любое число элементов любого типа.{' '}
      </p>
      <CopyBlock
        text={`// Пустой массив
const arr = [];

// Массив clients с тремя элементами
const clients = ['Mango', 'Poly', 'Ajax'];

console.log(clients); // ["Mango", "Poly", "Ajax"]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Доступ к элементам</h3>
      <p className={styles.description}>
        В качестве ключей-индексов используются цифры, индексация начинается с <b>нуля</b>.
      </p>
      <img className={styles.img} src={indicesImg} alt="array indices" />
      <p className={styles.description}>
        Чтобы получить нужный элемент из массива, после имени переменной которая содержит массив, указывается индекс
        элемента в квадратных скобках. На место такого выражения будет подставлено значение хранящееся в элементе
        массива.
      </p>
      <CopyBlock
        text={`const clients = ['Mango', 'Poly', 'Ajax'];

// Указывая в скобках индекс элемента мы получаем его значение
console.log(clients[0]); // Mango
console.log(clients[1]); // Poly
console.log(clients[2]); // Ajax`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.3 - Переопределение</h3>
      <p className={styles.description}>
        Элементы массива можно заменять и добавлять, обращаясь к элементу массива по индексу.
      </p>
      <CopyBlock
        text={`const clients = ['Mango', 'Poly', 'Ajax'];

// Значение элемента можно заменить
clients[0] = 'Kiwi';
console.log(clients[0]); // Kiwi

// Или добавить
clients[3] = 'Alex';
console.log(clients[3]); // Alex

console.log(clients); // ["Kiwi", "Poly", "Ajax", "Alex"]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.4 - Длина массива</h3>
      <p className={styles.description}>
        Длина массива динамическая величина и изменяется автоматически при добавлении или удалении элементов. Текущее
        число элементов массива содержится в его свойстве <code className={styles.code}>length</code>.
      </p>
      <p className={styles.description}>
        Свойство <code className={styles.code}>length</code> можно изменять напрямую.
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Если установить для свойства <code className={styles.code}>length</code> значение, превышающее количество
          элементов в массиве, новые элементы будут добавлены с начальными значениями{' '}
          <code className={styles.code}>undefined</code>.
        </li>
        <li className={styles.list__item}>
          Если задать длину массива меньше чем текущее кол-во элементов в массиве то все "не влезшие" элементы в новую
          длину просто удаляются.
        </li>
      </ul>
      <CopyBlock
        text={`const clients = ['Mango', 'Poly', 'Ajax'];

// length вернет текущую длину массива
console.log(clients.length); // 3

clients.length = 5;
console.log(clients.length); // 5
console.log(clients); // ["Mango", "Poly", "Ajax", empty × 2]
console.log(clients[4]); // undefined

clients.length = 2;
console.log(clients); // ["Mango", "Poly"]`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.5 - Итерация по массиву</h3>
      <p className={styles.description}>
        Для итерации по массиву или перебора массива, используются циклы, в частности цикл{' '}
        <code className={styles.code}>for</code>. Переберем массив клиентов и выведем их имена.
      </p>
      <CopyBlock
        text={`const clients = ['Mango', 'Ajax', 'Poly'];

for (let i = 0; i < clients.length; i += 1) {
  console.log('Logging clients: ', clients[i]);
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>С помощью цикла массив можно заполнить данными. </p>
      <CopyBlock
        text={`const numbers = [];

for (let i = 0; i < 3; i += 1) {
  numbers.push(\`label-$ {i}\`);
}

console.log('numbers: ', numbers); // ['label-0', 'label-1', 'label-2']`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.5.1 - Цикл for...of</h4>
        <p className={styles.description}>
          Инструкия <code className={styles.code}>for...of</code> создает цикл, перебирающий итерируемые объекты, такие
          как массивы и строки. Тело цикла будет выполняться для значения каждого отдельного элемента. Это хорошая
          замена циклу <code className={styles.code}>for</code> если не нужен доступ к счетчику итерации.
        </p>
        <CopyBlock
          text={`for (const variable of iterable) {
  // statement
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>variable</code> — для каждой итерации значение свойства присваивается
            переменной.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>iterable</code> — коллекция, которая имеет перечислимые свойства.
          </li>
        </ul>
        <CopyBlock
          text={`// Итерация по массиву
const clients = ['Mango', 'Ajax', 'Poly'];

for (const client of clients) {
  console.log(client);
}

// Итерация по строке
const string = 'javascript';

for (const character of string) {
  console.log(character);
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.5.2 - Инструкции break и continue</h4>
        <p className={styles.description}>
          Будем искать имя клиента в массиве имен, если нашли прервем цикл так как нет смысла искать дальше, имена у нас
          уникальные.
        </p>
        <CopyBlock
          text={`const clients = ['Mango', 'Poly', 'Ajax'];
const clientNameToFind = 'Poly';
let message;

for (const client of clients) {
  /*
   * На каждой итерации мы будем проверять совпадает ли элемент массива с
   * именем клиента. Если совпадает то мы записываем в message сообщение
   * об успехе и делаем break чтобы не искать дальше
   */

  if (client === clientNameToFind) {
    message = 'Клиент с таким именем есть в базе данных!';
    break;
  }

  // Если они не совпадают то запишем в resultMsg сообщeние об отсутсвии имени
  message = 'Клиента с таким именем нету в базе данных!';
}

console.log(message); // Клиент с таким именем есть в базе данных!`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>
          Можно изначально задать <code className={styles.code}>message</code> значение неудачи поиска, а в цикле
          перезаписать его на успех если нашли имя. Но <code className={styles.code}>break</code> всеравно пригодится,
          так как если у нас массив из 10000 клиентов а нужный нам стоит на позиции 2, то нет абсолютно никакого смысла
          перебирать оставшиеся 9998 элементов.{' '}
        </p>
        <CopyBlock
          text={`const clients = ['Mango', 'Poly', 'Ajax'];
const clientNameToFind = 'Poly';
let message = 'Клиента с таким именем нету в базе данных!';

for (const client of clients) {
  if (client === clientNameToFind) {
    message = 'Клиент с таким именем есть в базе данных!';
    break;
  }

  // Если не совпадает, то на этой итерации ничего не делаем
}

console.log(message); // Клиент с таким именем есть в базе данных!`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
        <p className={styles.description}>Используем цикл для вывода только чисел больше определенного значения. </p>
        <CopyBlock
          text={`/*
 * Для чисел меньше чем порог срабатывает continue, выполнение тела прекращается
 * и управление передаётся на следующую итерацию.
 */
const numbers = [1, 3, 14, 18, 4, 7, 29, 6, 34];
const threshold = 15;

for (let i = 0; i < numbers.length; i += 1) {
  if (numbers[i] < threshold) {
    continue;
  }

  console.log(\`Число больше чем $ {threshold}: $ {numbers[i]}\`); // 18, 29, 34
}`}
          language="javascript"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.6 - Многомерные массивы</h3>
      <p className={styles.description}>
        Массивы могут содержать другие массивы как элементы. Это можно использовать для создания матриц.
      </p>
      <CopyBlock
        text={`const matrix = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
];

console.log(matrix[0][0]); // 1
console.log(matrix[1][2]); // 6
console.log(matrix[2][2]); // 9`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>Для того чтобы перебрать такой массив используются вложенные циклы. </p>
      <CopyBlock
        text={`const matrix = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
];
let total = 0;

for (let i = 0; i < matrix.length; i += 1) {
  console.log('Подмассив матрицы matrix[i]: ', matrix[i]);

  for (let j = 0; j < matrix[i].length; j += 1) {
    console.log('Элемент подмассива матрицы matrix[i][j]: ', matrix[i][j]);
    total += matrix[i][j];
  }
}

console.log(total); // 45`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
  </section>
))
